import React, { useState } from 'react';
import Card from '../components/ui/Card';
import { useAppContext } from '../contexts/AppContext';
import ActiveProjectBadge from '../components/ActiveProjectBadge';
import { normalizeProjectName, extractProjectName } from '../utils/validation';
import { ModuleInfoModal } from '../components/ModuleInfoModal';
import { Edit2, Plus, Save, X, Trash2 } from 'lucide-react';
import { BomItem } from '../types';

import { ModalWrapper } from '../components/ModalWrapper';
import { useProjectFilter } from '../hooks/useProjectFilter';

// Modal for editing/adding BOM items
const EditBomModal: React.FC<{
    item: Partial<BomItem>;
    onSave: (item: BomItem) => void;
    onClose: () => void;
    onDelete?: (id: number) => void;
}> = ({ item, onSave, onClose, onDelete }) => {
    const { activeProject, allData } = useAppContext();
    const [formData, setFormData] = useState<Partial<BomItem>>({
        id: Math.max(0, ...(allData.bomItems || []).map(i => i.id)) + 1,
        component: '',
        description: '',
        basicFunction: '',
        material: '',
        quantity: 1,
        projectName: activeProject === 'all' ? 'Aerator Pro' : activeProject,
        epochId: allData.projectMetadata?.epochId || 1,
        ...item
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData as BomItem);
    };

    return (
        <ModalWrapper
            isOpen={true}
            onClose={onClose}
            title={
                <>
                    {item.id ? <Edit2 size={18} className="text-primary" /> : <Plus size={18} className="text-primary" />}
                    {item.id ? 'Edit Component' : 'Add New Component'}
                </>
            }
            maxWidth="max-w-[500px]"
        >
            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="block text-xs font-bold text-text-secondary uppercase mb-1">ID</label>
                        <input
                            type="number"
                            value={formData.id}
                            onChange={e => setFormData({ ...formData, id: parseInt(e.target.value) })}
                            className="w-full p-2 rounded border border-border bg-card text-text-primary"
                            disabled={!!item.id}
                        />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-text-secondary uppercase mb-1">Quantity</label>
                        <input
                            type="number"
                            value={formData.quantity}
                            onChange={e => setFormData({ ...formData, quantity: parseInt(e.target.value) })}
                            className="w-full p-2 rounded border border-border bg-card text-text-primary"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-xs font-bold text-text-secondary uppercase mb-1">Component Name</label>
                    <input
                        type="text"
                        value={formData.component}
                        onChange={e => setFormData({ ...formData, component: e.target.value })}
                        className="w-full p-2 rounded border border-border bg-card text-text-primary"
                        required
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-text-secondary uppercase mb-1">Description / Material</label>
                    <input
                        type="text"
                        value={formData.description}
                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                        className="w-full p-2 rounded border border-border bg-card text-text-primary"
                    />
                </div>

                <div>
                    <label className="block text-xs font-bold text-text-secondary uppercase mb-1">Basic Function</label>
                    <textarea
                        value={formData.basicFunction}
                        onChange={e => setFormData({ ...formData, basicFunction: e.target.value })}
                        className="w-full p-2 rounded border border-border bg-card text-text-primary h-20 resize-none"
                        placeholder="Primary purpose of this component..."
                    />
                </div>

                <div className="flex justify-between items-center pt-4 border-t border-border">
                    {item.id && onDelete ? (
                        <button
                            type="button"
                            onClick={() => { if (window.confirm('Delete this component?')) onDelete(item.id!); }}
                            className="px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg flex items-center gap-2"
                        >
                            <Trash2 size={16} /> Delete
                        </button>
                    ) : <div />}

                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-text-secondary hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-4 py-2 text-sm font-bold text-white bg-primary hover:bg-primary-dark rounded-lg flex items-center gap-2 shadow-lg"
                        >
                            <Save size={16} /> {item.id ? 'Update' : 'Create'}
                        </button>
                    </div>
                </div>
            </form>
        </ModalWrapper>
    );
};


const SystemDefinition: React.FC = () => {
    const { highlightedItem, navigate, allData, activeProject, updateBomItem, deleteBomItem } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingItem, setEditingItem] = useState<Partial<BomItem> | null>(null);
    const { isItemInProject } = useProjectFilter();

    const filteredBomItems = (allData.bomItems || []).filter(item => isItemInProject(item.projectName, item.epochId));

    const handleEdit = (item: BomItem, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingItem(item);
        setIsModalOpen(true);
    };

    const handleAddNew = () => {
        setEditingItem({});
        setIsModalOpen(true);
    };

    const handleSave = async (item: BomItem) => {
        await updateBomItem(item);
        setIsModalOpen(false);
        setEditingItem(null);
    };

    const handleDelete = async (id: number) => {
        await deleteBomItem(id);
        setIsModalOpen(false);
        setEditingItem(null);
    };

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-3xl font-bold text-text-primary">System Definition (Bill of Materials)</h1>
                    <p className="text-text-secondary mt-1">Complete list of components{activeProject !== 'all' ? ` for ${activeProject}` : ''}</p>
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleAddNew}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white font-bold rounded-lg hover:bg-primary-dark shadow-lg transition-all"
                    >
                        <Plus size={18} /> New Component
                    </button>
                    <ModuleInfoModal
                        title="System Definition (BOM)"
                        description={`The System Definition page displays the complete Bill of Materials (BOM) for your ${activeProject !== 'all' ? 'selected project' : 'projects'}. This is the foundation of your reliability analysis - every component listed here can be mapped to functions, controls, and FMEAs.`}
                        description2={`Currently showing ${filteredBomItems.length} component${filteredBomItems.length !== 1 ? 's' : ''}. Each BOM item represents a physical part or assembly in your system. Click any component to navigate to the BOM Functions view where you can see its basic function and related controls.`}
                        benefits={[
                            `Centralized inventory of all ${filteredBomItems.length} system components`,
                            "Foundation for traceability: BOM → Functions → Controls → FMEAs",
                            "Quick component lookup by ID or name",
                            "Clickable rows for easy navigation to detailed function mapping",
                            "Material and description fields for engineering reference",
                            "Project-filtered view ensures you see only relevant components"
                        ]}
                        usageInstructions={[
                            "Review all components to ensure complete BOM coverage for the system",
                            "Click any row to jump to BOM Functions view with that component highlighted",
                            "Verify Component IDs match your engineering drawings and PLM system",
                            "Use this view during design reviews to confirm all parts are accounted for",
                            "Cross-reference with P-Diagrams to ensure all system elements have BOM entries",
                            "Export or screenshot this table for documentation and reviews"
                        ]}
                    />
                    <ActiveProjectBadge showStandard={false} />
                </div>
            </div>

            <Card title="Bill of Materials (BOM)">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left text-text-secondary">
                        <thead className="text-xs text-text-primary uppercase bg-background/50">
                            <tr>
                                <th scope="col" className="px-6 py-3 w-32">Component ID</th>
                                <th scope="col" className="px-6 py-3">Component Name</th>
                                <th scope="col" className="px-6 py-3">Description / Material</th>
                                <th scope="col" className="px-6 py-3 w-20 text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBomItems.map((item) => {
                                const isHighlighted = highlightedItem?.type === 'bom' && highlightedItem.id === item.id;
                                return (
                                    <tr
                                        key={item.id}
                                        className={`border-b border-border hover:bg-background cursor-pointer ${isHighlighted ? 'animate-highlight' : ''}`}
                                        onClick={() => navigate('/bom-functions', { highlight: { type: 'bom', id: item.id } })}
                                    >
                                        <td className="px-6 py-4 font-medium">{item.id}</td>
                                        <td className="px-6 py-4 text-text-primary font-semibold">{item.component}</td>
                                        <td className="px-6 py-4">{item.description}</td>
                                        <td className="px-6 py-4 text-center">
                                            <button
                                                onClick={(e) => handleEdit(item, e)}
                                                className="p-1.5 text-primary hover:bg-primary/10 rounded-full transition-colors"
                                                title="Edit item"
                                            >
                                                <Edit2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {filteredBomItems.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="px-6 py-12 text-center text-text-secondary">
                                        No components found for this project.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            {isModalOpen && editingItem && (
                <EditBomModal
                    item={editingItem}
                    onSave={handleSave}
                    onClose={() => setIsModalOpen(false)}
                    onDelete={handleDelete}
                />
            )}
        </div>
    );
};

export default SystemDefinition;
